import React, { useMemo } from 'react';

import {
  integerToCurrency,
} from 'loot-core/shared/util';
import { type CategoryGroupEntity } from 'loot-core/types/models';

import { type CSSProperties, theme } from '../../style';
import { View } from '../common/View';
import { useFormat } from '../spreadsheet/useFormat';

import { type ITableTotals, type VisibleTotals } from './types';
import { VIEW_MODE } from './util';

const expensesHeaderStyle: CSSProperties = {
  flexDirection: 'row',
  fontWeight: 600,
  minWidth: 'fit-content',
  marginTop: 20,
};

const childStyle: CSSProperties = {
  backgroundColor: theme.tableRowHeaderAlternativeBackground,
  color: theme.tableRowHeaderAlternativeText,
  height: '30px',
  alignItems: 'center',
  borderTopWidth: 1,
  borderColor: theme.tableBorder,
  flexDirection: 'row',
  justifyContent: 'flex-end',
};

type IExpensesTotalHeaderProps = {
  year: number;
  categoryGroups: CategoryGroupEntity[];
  groupTotals: { [groupId: string]: ITableTotals };
  visibleTotals: VisibleTotals;
  months: { index: number; month: string }[];
  viewMode: VIEW_MODE;
};

export function ExpenseHeader({
  year,
  categoryGroups,
  groupTotals,
  visibleTotals,
  months,
  viewMode,
}: IExpensesTotalHeaderProps) {
  const format = useFormat();

  const totals = useMemo(() => {
    const tmpTotals = {
      monthly: 0,
      yearly: 0,
    };
    for (const groupId of Object.keys(groupTotals)) {
      const catGroup = categoryGroups.find(o => o.id === groupId);
      if (!catGroup.is_income) {
        tmpTotals.monthly += groupTotals[groupId].monthly;
        tmpTotals.yearly += groupTotals[groupId].yearly;
      }
    }
    return tmpTotals;
  }, [groupTotals]);

  return (
    <View style={expensesHeaderStyle}>
      <View
        style={{
          ...childStyle,
          justifyContent: 'flex-start',
          paddingLeft: 17,
          width: '200px',
          position: 'sticky',
          left: 0,
          zIndex: 1000,
        }}
      >
        Expenses
      </View>
      {viewMode !== VIEW_MODE.ONLY_MONTHS && (
        <View
          style={{
            flexDirection: 'row',
            position: 'sticky',
            left: 200,
            zIndex: 1000,
            ...childStyle,
          }}
        >
          <View style={{ width: '120px' }} />
          <View style={{ width: '120px' }} />
          <View
            style={{ width: '120px', textAlign: 'right', padding: '0 4px' }}
          >
            {integerToCurrency(totals.monthly)}
          </View>
          <View
            style={{ width: '120px', textAlign: 'right', padding: '0 4px' }}
          >
            {integerToCurrency(totals.yearly)}
          </View>
          <View style={{ width: '60px' }} />
        </View>
      )}
      <View
        style={{
          flexDirection: 'row',
          flex: 1,
        }}
      >
        <View
          style={{
            ...childStyle,
            textAlign: 'right',
            minWidth: '100px',
            flex: 1,
            padding: '0 4px',
          }}
        >
          <View style={{ color: theme.pageTextLight, fontWeight: 600 }}>
            {format(
              Math.round(visibleTotals.pageYear.expenseMonthlyAvg),
              'financial',
            )}
          </View>
        </View>
        <View
          style={{
            ...childStyle,
            textAlign: 'right',
            minWidth: '100px',
            flex: 1,
            padding: '0 4px',
          }}
        >
          <View style={{ color: theme.pageTextLight, fontWeight: 600 }}>
            {format(visibleTotals.pageYear.expense, 'financial')}
          </View>
        </View>
        {months.map(o => (
          <View
            key={o.index}
            style={{
              ...childStyle,
              textAlign: 'right',
              minWidth: '100px',
              flex: 1,
              padding: '0 4px',
            }}
          >
            <View style={{ color: theme.pageTextLight, fontWeight: 600 }}>
              {format(
                visibleTotals.pageMonth.get(o.month)?.expense ?? 0,
                'financial',
              )}
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}
